<template>
  <div ref="chartContainer" style="width: 100%; max-width: 2200px; height: 400px;"></div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'LineChart',

  props: {
    totalDistance: {
      type: Array,
      required: true
    },
    averageSpeed: {
      type: Array,
      required: true
    },
    speedZone: {
      type: Array,
      required: true
    },
    sprintCount: {
      type: Array,
      required: true
    },
    speedDistance: {
      type: Array,
      required: true
    },
    chartMatchesDates: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      resizeObserver: null
    };
  },

mounted() {
  this.initChart();
  window.addEventListener('resize', this.resizeChart);

  this.chart.on('legendselectchanged', (params) => {
    this.updateSeriesLabels(params.selected);
  });

  this.resizeObserver = new ResizeObserver(entries => {
    for (let entry of entries) {
      if (this.chart && entry.target === this.$refs.chartContainer) {
        this.chart.resize();
      }
    }
  });

  this.resizeObserver.observe(this.$refs.chartContainer); // 👉 ОБЯЗАТЕЛЬНО
},


beforeDestroy() {
  window.removeEventListener('resize', this.resizeChart);

  if (this.resizeObserver) {
    this.resizeObserver.disconnect(); // 👉 ОЧИСТКА
  }
},


  methods: {
    updateSeriesLabels(selected) {
      const activeSeries = Object.entries(selected)
        .filter(([, isSelected]) => isSelected)
        .map(([name]) => name);

      const onlyOneVisible = activeSeries.length === 1;

      this.chart.setOption({
        series: this.chart.getOption().series.map(series => ({
          ...series,
          label: {
            show: onlyOneVisible && activeSeries.includes(series.name),
            position: 'top',
            color: series.lineStyle?.color || '#fff'
          }
        }))
      });
    },


    getGlobalMinMax(seriesData) {
      const allValues = seriesData.flatMap(s => s.data);
      const min = Math.min(...allValues);
      const max = Math.max(...allValues);
      return {
        min: min - 5,
        max: max + 5
      };
    },

    getIndividualMinMax(seriesData) {
      return seriesData.map(s => {
        const filteredData = s.data.filter(v => typeof v === 'number' && v > 0); // исключаем нули и отрицательные числа
        const min = Math.min(...filteredData);
        const max = Math.max(...filteredData);
        return {
          min: min > 0 ? min * 0.9 : 0.1, // небольшая подстройка для визуализации
          max: max * 1.1
        };
      });
    },

    initChart() {
      const chart = echarts.init(this.$refs.chartContainer);

      // Пример данных для графика
      const data = {
        xAxis: this.chartMatchesDates,
        series: [
          { name: 'Загальна дистанція', data: this.totalDistance, color: '#DA4167' },
          { name: 'Середня швидкість', data: this.averageSpeed, color: '#339989' },
          { name: 'Швидкісна зона (25.20+)', data: this.speedZone, color: '#8FC408' },
          { name: 'Кількість спринтів (25.20+)', data: this.sprintCount, color: '#C59014' },
          { name: 'Швидкісний метраж', data: this.speedDistance, color: '#1B4079' },
        ]
      };
      

      const yBounds = this.getIndividualMinMax(data.series);

      // Настройки графика
      const option = {
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: ['Загальна дистанція', 'Середня швидкість', 'Швидкісна зона (25.20+)', 'Кількість спринтів (25.20+)', 'Швидкісний метраж'],
          color: '#D9D9D9',
          bottom: 25,
          textStyle: {
            color: '#D9D9D9'
          }
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: data.xAxis,
          axisLine: {
            show: false, // Убрать нижнюю линию
            onZero: false
          },
          axisTick: {
            show: true // Засечки оставить
          },
          axisLabel: {
            show: true,
            color: '#D9D9D9' // Даты оставить
          }
        },
        grid: {
          left: 50,
          top: 20,
          right: 50,
          bottom: 100
        },
        yAxis: data.series.map((line, index) => ({
          type: 'log',
          name: line.name,
          position: 'right',
          axisLabel: { formatter: '{value}' },
          min: yBounds[index].min,
          max: yBounds[index].max,
          show: false
        })),


        tooltip: {
          trigger: 'axis',
          formatter: function (params) {
            return params.map(item => {
              return `
                <div style="margin-bottom: 5px; color: #D9D9D9; padding: 5px; border-radius: 20px;">
                  <span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background:${item.borderColor};"></span>
                  ${item.seriesName}: ${item.data}
                </div>
              `;
            }).join('');
          },
          backgroundColor: '#746F72',
          borderColor: '#746F72',
          borderRadius: 12,
          textStyle: {
            color: '#000'
          }
        },
        series: data.series.map((line, index) => ({
          name: line.name,
          type: 'line',
          data: line.data,
          stack: null,
          symbol: "circle",
          symbolSize: 10,
          itemStyle: {
            color: '#0E1420',
            borderColor: line.color,
            borderWidth: 3
          },
          lineStyle: {
            width: 5,
            color: line.color
          },
          label: {
            show: false,
            position: 'top',
            color: line.color,
            formatter: function (params) {
              return params.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
            }
          },
          yAxisIndex: index
        }))
      };

      chart.setOption(option);
      this.chart = chart; // Сохраняем ссылку на объект графика
    },

    resizeChart() {
      if (this.chart) {
        this.chart.resize();
      }
    }
  }
};
</script>

<style scoped>
/* Стили для компонента графика */
</style>
